'use client';

import { type ReactElement, cloneElement, forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { AssistChipContext } from './context/assist-chip.context';
import { assistChipAvatarStyles } from './styles/assist-chip-avatar.styles';
import type { AssistChipAvatarPropsInterface } from './types/assist-chip-avatar.types';

/**
 * @since 10.0.0
 */
export const AssistChipAvatar = forwardRef<HTMLDivElement, AssistChipAvatarPropsInterface>(
  (props, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { loading } = useContext(AssistChipContext);

    /*
     * Don't render avatar when loading is true
     */
    if (loading) {
      return null;
    }

    const styles = css(assistChipAvatarStyles, cssProp);

    /*
     * Prevent img alt text from being read and
     * chip from being announced as a "button, group"
     */
    const adjustedAvatar = cloneElement(children as ReactElement, {
      size: 'xs',
    });

    return (
      <span
        data-c11n-component="AssistChip.Avatar"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        className={cx('c11n', 'c11n_assist-chip__avatar', styles, className)}
        {...rest}
      >
        {adjustedAvatar}
      </span>
    );
  },
);

AssistChipAvatar.displayName = 'AssistChip.Avatar';
